/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/column-chooser.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridLabels } from '../types'

const CLASS_NAME_COLUMN_CHOOSER = 'data-grid-column-chooser'
const CLASS_NAME_OPTION = 'data-grid-column-chooser-option'
const CLASS_NAME_FOOTER = 'data-grid-column-chooser-footer'

export interface ColumnChooserColumn {
  id: string
  label: string
  canHide: boolean
  visible: boolean
}

export interface GridColumnChooserContext {
  root: HTMLElement
  labels: DataGridLabels
  getColumns: () => ColumnChooserColumn[]
  toggleVisibility: (columnId: string, visible: boolean) => void
  showAll: () => void
  reset: () => void
}

export interface GridColumnChooser {
  toggle: (button: HTMLElement) => void
  close: () => void
  dispose: () => void
}

let columnChooserCount = 0

export const createColumnChooser = (context: GridColumnChooserContext): GridColumnChooser => {
  const element = document.createElement('div')
  element.classList.add(CLASS_NAME_COLUMN_CHOOSER)
  element.setAttribute('role', 'dialog')
  element.setAttribute('aria-label', context.labels.toolbarColumns)
  element.id = `data-grid-column-chooser-${++columnChooserCount}`

  let trigger: HTMLElement | null = null

  const close = (refocus = false) => {
    if (!trigger) {
      return
    }

    trigger.setAttribute('aria-expanded', 'false')
    trigger.removeAttribute('aria-controls')
    if (refocus) {
      trigger.focus()
    }

    trigger = null
    element.remove()
    document.removeEventListener('pointerdown', onOutsidePointerDown, true)
    window.removeEventListener('scroll', onWindowChange, true)
    window.removeEventListener('resize', onWindowChange)
  }

  const onOutsidePointerDown = (event: Event) => {
    const target = event.target as Node
    if (!element.contains(target) && !trigger?.contains(target)) {
      close()
    }
  }

  const onWindowChange = (event?: Event) => {
    if (event?.target instanceof Node && element.contains(event.target)) {
      return
    }

    close()
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close(true)
    }
  }

  element.addEventListener('keydown', onKeydown)

  const buildContent = () => {
    const list = document.createElement('div')

    for (const column of context.getColumns()) {
      const label = document.createElement('label')
      label.classList.add('form-check', CLASS_NAME_OPTION)
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.classList.add('form-check-input')
      input.checked = column.visible
      input.dataset.coreuiColumnChooserOption = column.id
      if (!column.canHide) {
        input.disabled = true
        input.setAttribute('aria-disabled', 'true')
      }

      input.addEventListener('change', () => {
        context.toggleVisibility(column.id, input.checked)
      })
      const text = document.createElement('span')
      text.classList.add('form-check-label')
      text.textContent = column.label
      label.append(input, text)
      list.append(label)
    }

    const footer = document.createElement('div')
    footer.classList.add(CLASS_NAME_FOOTER)

    const showAll = document.createElement('button')
    showAll.type = 'button'
    showAll.classList.add('btn', 'btn-link', 'btn-sm')
    showAll.textContent = context.labels.showAllColumns
    showAll.dataset.coreuiColumnChooserShowAll = ''
    showAll.addEventListener('click', () => {
      context.showAll()
      element.replaceChildren(buildContent())
    })

    const reset = document.createElement('button')
    reset.type = 'button'
    reset.classList.add('btn', 'btn-link', 'btn-sm')
    reset.textContent = context.labels.resetColumns
    reset.dataset.coreuiColumnChooserReset = ''
    reset.addEventListener('click', () => {
      context.reset()
      element.replaceChildren(buildContent())
    })

    footer.append(showAll, reset)

    const content = document.createDocumentFragment()
    content.append(list, footer)
    return content
  }

  const open = (button: HTMLElement) => {
    close()
    element.replaceChildren(buildContent())

    trigger = button
    button.setAttribute('aria-expanded', 'true')
    button.setAttribute('aria-controls', element.id)
    context.root.append(element)

    const rootRect = context.root.getBoundingClientRect()
    const buttonRect = button.getBoundingClientRect()
    element.style.top = `${buttonRect.bottom - rootRect.top}px`
    element.style.left = `${Math.max(buttonRect.left - rootRect.left, 0)}px`

    document.addEventListener('pointerdown', onOutsidePointerDown, true)
    window.addEventListener('scroll', onWindowChange, true)
    window.addEventListener('resize', onWindowChange)
    element.querySelector<HTMLElement>('input:not(:disabled)')?.focus()
  }

  return {
    toggle(button) {
      if (trigger === button) {
        close(true)
        return
      }

      close()
      open(button)
    },
    close() {
      close()
    },
    dispose() {
      close()
      element.removeEventListener('keydown', onKeydown)
    }
  }
}
